import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { CalendarCheck } from 'lucide-react';
import { clsx } from 'clsx';
import Collection from '@model-core/core/Collection';
import { CardGrid } from '@/components/list/CardGrid';
import { Panel } from '@/components/Panel';
import { ListStatus } from '@/components/list/ListStatus';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { usePagedList } from '@/hooks/usePagedList';
import { useRelatedByIds } from '@/hooks/useRelatedByIds';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { todayIn } from '@/lib/companyTime';
import { formatMinutes } from '@/lib/duration';
import PeriodModel from '@/models/entities/PeriodModel';
import PeriodStatusHistoryModel from '@/models/entities/PeriodStatusHistoryModel';
import { PERIOD_STATUS } from '@/models/references/references';
import { useCompanyStore, useCompanyTimezone, useCurrentCompany } from '@/stores/useCompanyStore';
import { findReferenceId, useReferenceOptions } from '@/stores/useReferenceOptions';
import { periodFilters } from './historyQuery';
import { YearPicker } from './YearPicker';
import { PERIOD_SUMMARIES } from '@/models/entities/PeriodSummaryModel';
import { teamTotalsLoader, type PeriodTotals } from '@/models/entities/PeriodTeamSummaryModel';
import { reviewerOf } from '@/components/timesheet/reviewActions';

/** Au plus 12 périodes par vue (les 12 dernières, ou une année) */
const HISTORY_PAGE_SIZE = 12;

/**
 * Dates de clôture des périodes affichées (une requête pour la vue) : [periodId => ligne Clôturée de l'historique].
 * Sans historique (erreur) : les tuiles restent affichées, sans la date de clôture.
 */
function useClosingDates(periodIds: number[], closedStatusId: number | null) {
  const load = useCallback(
    async (ids: string[]) => {
      const { collection } = await new Collection(PeriodStatusHistoryModel)
        .setFilter('period', ids as never)
        .setFilter('periodStatus', String(closedStatusId))
        .list();
      return new Map(collection.models.map((history) => [history.periodId, history]));
    },
    [closedStatusId],
  );

  return useRelatedByIds(periodIds, closedStatusId ? load : null);
}

/**
 * Historique (§4.10), vue du Dirigeant ou du manager : une tuile par période clôturée de la société sélectionnée, avec le
 * total d'heures et le nombre de demandes (feuilles soumises), tenus par l'API : toutes équipes confondues pour le
 * Dirigeant (PeriodSummary), ses équipes seulement pour le manager (somme de leurs PeriodTeamSummary).
 * De la plus récente à la plus ancienne : les 12 dernières périodes clôturées par défaut (« 12 derniers mois », sans
 * filtre de date), ou une année (YearPicker), filtre envoyé à l'API. Jamais plus de 12 périodes : pas de pagination, une seule requête, sans comptage (partial=true).
 * Une tuile ouvre le récapitulatif de la période (/history/{id}, détail par collaborateur) ; l'export viendra plus tard.
 */
export function HistoryPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);
  const closedStatusId = findReferenceId(useReferenceOptions('periodStatuses'), PERIOD_STATUS.CLOSED);
  const [year, setYear] = useState<number | null>(null);
  // Dernière année du year picker : l'année en cours à l'heure de la société, pas du navigateur
  const currentYear = Number(todayIn(useCompanyTimezone()).slice(0, 4));
  // Le manager : les totaux de ses équipes (null : le Dirigeant, ceux de la société)
  const reviewer = useMemo(() => reviewerOf(company), [company]);
  const scopeKey = reviewer.isDirector ? '' : reviewer.managedTeamIds.join(',');

  // Autre société : retour aux 12 derniers mois
  const companyId = company?.id;
  const [shownCompanyId, setShownCompanyId] = useState(companyId);
  if (shownCompanyId !== companyId) {
    setShownCompanyId(companyId);
    setYear(null);
  }

  const request = useMemo(
    () =>
      companyId && closedStatusId
        ? {
            filters: { company: companyId, periodStatus: closedStatusId, ...periodFilters(year) },
            order: { month: 'desc' as const },
            page: 1,
            itemsPerPage: HISTORY_PAGE_SIZE,
            partial: true,
          }
        : null,
    [companyId, closedStatusId, year],
  );
  const {
    models: periods,
    loading,
    error,
  } = usePagedList(PeriodModel, request, 0, scopeKey ? undefined : { summary: PERIOD_SUMMARIES });
  const periodIds = useMemo(() => periods.map((period) => Number(period.id)), [periods]);
  const closings = useClosingDates(periodIds, closedStatusId);
  const teamTotals = useRelatedByIds(
    periodIds,
    useMemo(() => (scopeKey ? teamTotalsLoader(scopeKey.split(',').map(Number)) : null), [scopeKey]),
  );
  const totalsOf = (period: PeriodModel): PeriodTotals | undefined =>
    scopeKey
      ? teamTotals?.get(Number(period.id))
      : { timeSheetCount: period.timeSheetCount, totalMinutes: period.totalMinutes };

  if (companiesLoaded && !company) {
    return <NoCompanySelected />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-4">
        {scopeKey && <p className="mr-auto text-sm text-slate-500">Les totaux de vos équipes.</p>}
        <YearPicker value={year} max={currentYear} onChange={setYear} />
      </div>

      <ListStatus
        loading={!request || (loading && !periods.length)}
        error={error}
        empty={
          !loading &&
          periods.length === 0 &&
          (year ? `Aucune période clôturée en ${year}.` : 'Aucune période clôturée.')
        }
      >
        <CardGrid loading={loading} data-testid="period-cards">
          {periods.map((period) => (
            <PeriodCard
              key={period.id}
              period={period}
              totals={totalsOf(period)}
              closing={closings?.get(Number(period.id))}
            />
          ))}
        </CardGrid>
      </ListStatus>
    </div>
  );
}

function PeriodCard({
  period,
  totals,
  closing,
}: {
  period: PeriodModel;
  /** Ceux de la société ou de ses équipes ; absents pendant leur chargement */
  totals: PeriodTotals | undefined;
  closing: PeriodStatusHistoryModel | undefined;
}) {
  return (
    <Panel
      as="article"
      aria-label={period.monthLabel}
      className="relative p-6 flex flex-col hover:border-blue-300 hover:shadow-md transition-all focus-within:ring-[3px] focus-within:ring-blue-500/30"
    >
      {/* Pas de badge de statut : l'écran ne liste que des périodes clôturées */}
      <div className="mb-4">
        <div className="inline-flex p-3 rounded-lg bg-blue-50 text-blue-600">
          <CalendarCheck className="w-6 h-6" />
        </div>
      </div>

      <h3 className="text-lg font-bold text-slate-900 mb-4">
        {/* Toute la tuile ouvre le récapitulatif de la période */}
        <Link to={`/history/${period.id}`} className="outline-none after:absolute after:inset-0 after:rounded-xl">
          {period.monthLabel}
        </Link>
      </h3>

      <dl className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-50 p-3 rounded-lg">
          <dt className="text-xs text-slate-500 mb-1">Total heures</dt>
          <dd className={clsx('font-bold', totals?.totalMinutes ? 'text-slate-900' : 'text-slate-400')}>
            {totals ? formatMinutes(totals.totalMinutes) : '–'}
          </dd>
        </div>
        <div className="bg-slate-50 p-3 rounded-lg">
          <dt className="text-xs text-slate-500 mb-1">Demandes</dt>
          <dd className="font-bold text-slate-900">{totals ? totals.timeSheetCount : '–'}</dd>
        </div>
      </dl>

      <p className="mt-auto pt-4 border-t border-slate-100 text-xs text-slate-400 min-h-[2.25rem]">
        {closing && `Clôturée le ${closing.createdAt.format(DATE_TIME_FORMAT)}`}
      </p>
    </Panel>
  );
}
